import { ChevronsLeft, ChevronDown } from '@music-vine/cadence/icons';
import { useRouter } from 'next/router';
import { memo, useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';
import { DynamicLink } from '@/components/DynamicLink';
import Discord from '@/components/Icons/SocialMono/Discord';
import Instagram from '@/components/Icons/SocialMono/Instagram';
import { UBLogo, UBLogoSmall } from '@/components/Logo';
import ThemeToggle from '@/components/ThemeToggle';
import Tooltip from '@/components/Tooltip';
import { Button, FadeAway } from '@music-vine/cadence/ui';
import { SidebarContext } from '@/providers/SidebarProvider';
import { currentTrackAtom } from '@/features/AudioPlayer/atoms';
import { searchOverlayAtom } from '@/features/GlobalSearch/searchStorage';
import useTWScreens from '@/hooks/ui/useTWScreens';
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, useSidebar } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { hidesSidebar } from '@/utils/page-chrome';
import AppSidebarLinks from './AppSidebarLinks';
import { AnimatePresence, motion } from 'motion/react';
import { useHomepageLink } from '@/hooks/auth';
import { logEvent } from '@/pages/api/v1/utils';

// Scroll detection constants
const SCROLL_THRESHOLD = 10;
const INITIAL_CHECK_DELAY = 100;
const SCROLL_THROTTLE_MS = 16; // ~60fps
const RESIZE_DEBOUNCE_MS = 150;
const MIN_GRADIENT_HEIGHT = 64; // 16 * 4 = 64px (h-16)
const MAX_GRADIENT_HEIGHT = 96; // 24 * 4 = 96px (h-24)
const SIDEBAR_FADE_CLASSES = '[--fade-away-from-dark:var(--ub-sidebar-bg)] [--fade-away-from:var(--ub-sidebar-bg)] [--fade-away-to-dark:transparent] [--fade-away-to:transparent]';
const SAFARI_REGEX = /^((?!chrome|android).)*safari/i;
const DISCORD_URL = 'https://discord.gg/q5xaCqhGFC';
const INSTAGRAM_URL = 'https://www.instagram.com/UppbeatOfficial/';
const AppSidebar = memo(() => {
  const router = useRouter();
  const {
    toggleSidebar,
    isSidebarToggled,
    toggleSearch
  } = useContext(SidebarContext);
  const [isSearchVisible, setIsSearchVisible] = useAtom(searchOverlayAtom);
  const {
    isMd
  } = useTWScreens();
  const currentTrack = useAtomValue(currentTrackAtom);
  const isAudioPlayerVisible = !!currentTrack;
  const {
    state
  } = useSidebar();
  const isSidebarCollapsed = state === 'collapsed';
  const homepageLink = useHomepageLink();

  // Scroll detection
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [canScrollUp, setCanScrollUp] = useState(false);

  // Detect Safari
  const isSafari = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return SAFARI_REGEX.test(navigator.userAgent);
  }, []);
  const onToggleSidebar = useCallback(() => {
    toggleSidebar();
  }, [toggleSidebar]);
  const onCloseSidebar = useCallback(() => {
    toggleSidebar(false);
  }, [toggleSidebar]);
  const onDiscordClick = useCallback(() => {
    void logEvent({
      event_type: 'click',
      asPath: router.asPath,
      metadata: {
        type: 'sidebar_social_discord',
        text: 'Discord',
        next_path: DISCORD_URL
      }
    });
  }, [router.asPath]);
  const onInstagramClick = useCallback(() => {
    void logEvent({
      event_type: 'click',
      asPath: router.asPath,
      metadata: {
        type: 'sidebar_social_instagram',
        text: 'Instagram',
        next_path: INSTAGRAM_URL
      }
    });
  }, [router.asPath]);
  const isSidebarDisabled = hidesSidebar(router.pathname);

  // Check if sidebar content is scrollable
  const checkScroll = useCallback(() => {
    if (!scrollContainerRef.current) {
      return;
    }
    const {
      scrollTop,
      scrollHeight,
      clientHeight
    } = scrollContainerRef.current;
    const hasOverflow = scrollHeight > clientHeight;
    const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD;
    setCanScrollDown(hasOverflow && !scrolledToBottom);
    setCanScrollUp(scrollTop > SCROLL_THRESHOLD);
  }, []);

  // Optimized event handlers
  const throttledScrollHandler = useMemo(() => throttle(checkScroll, SCROLL_THROTTLE_MS), [checkScroll]);

  // Track container height so the gradient recalculates on resize, not just
  // when canScrollDown toggles.
  const [containerHeight, setContainerHeight] = useState(0);

  // Update containerHeight from the resize handler
  const debouncedResizeWithHeight = useMemo(() => debounce(() => {
    checkScroll();
    if (scrollContainerRef.current) {
      setContainerHeight(scrollContainerRef.current.clientHeight);
    }
  }, RESIZE_DEBOUNCE_MS), [checkScroll]);

  // Check scroll on mount and when content changes
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }
    container.addEventListener('scroll', throttledScrollHandler, {
      passive: true
    });
    window.addEventListener('resize', debouncedResizeWithHeight);

    // Initial check after a short delay to ensure content is loaded
    const timer = setTimeout(() => {
      checkScroll();
      setContainerHeight(container.clientHeight);
    }, INITIAL_CHECK_DELAY);
    return () => {
      container.removeEventListener('scroll', throttledScrollHandler);
      window.removeEventListener('resize', debouncedResizeWithHeight);
      clearTimeout(timer);
      throttledScrollHandler.cancel();
      debouncedResizeWithHeight.cancel();
    };
  }, [throttledScrollHandler, debouncedResizeWithHeight, checkScroll]);

  // Scroll down when chevron is clicked
  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  // Calculate responsive gradient height based on container height
  const gradientHeight = useMemo(() => {
    if (!containerHeight) {
      return MAX_GRADIENT_HEIGHT;
    }
    return Math.min(MAX_GRADIENT_HEIGHT, Math.max(MIN_GRADIENT_HEIGHT, containerHeight * 0.15));
  }, [containerHeight]);

  // Mobile full-screen sidebar overlay (below 600px) — must render even on
  // homepage/pricing where the desktop sidebar is disabled
  const mobileSidebar = <AnimatePresence>
      {isSidebarToggled && <motion.div animate={{
      opacity: 1,
      x: 0
    }} className="fixed inset-0 z-50 flex headerFixed:hidden flex-col bg-[var(--ub-sidebar-bg)]" exit={{
      opacity: 0,
      x: -20
    }} initial={{
      opacity: 0,
      x: -20
    }} style={{
      marginTop: '52px'
    }} transition={{
      duration: 0.3,
      ease: [0.32, 0.72, 0, 1]
    }}>
          <div className="flex-1 overflow-y-auto">
            <AppSidebarLinks />
          </div>
          <div className="flex items-center justify-between px-4 py-5">
            <ThemeToggle />
            <div className="flex items-center gap-1">
              <a aria-label="Follow Uppbeat on Instagram" className="flex size-7 items-center justify-center rounded-full text-gray-800 hover:bg-gray-50 dark:text-white dark:hover:bg-gray-900" href={INSTAGRAM_URL} onClick={onInstagramClick} rel="noreferrer" target="_blank">
                <Instagram className="size-4" />
              </a>
              <div className="h-4 w-px bg-gray-150 dark:bg-gray-800" />
              <a aria-label="Join the Uppbeat Discord server" className="flex size-7 items-center justify-center rounded-full text-gray-800 hover:bg-gray-50 dark:text-white dark:hover:bg-gray-900" href={DISCORD_URL} onClick={onDiscordClick} rel="noreferrer" target="_blank">
                <Discord className="size-4" />
              </a>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
  if (isSidebarDisabled) {
    return mobileSidebar;
  }
  return <>
      {mobileSidebar}

      <Sidebar className="bg-[var(--ub-sidebar-bg)] [&_[data-sidebar=sidebar]]:bg-[var(--ub-sidebar-bg)]" collapsible="icon" side="left" variant="sidebar">
        {/* Logo */}
        <SidebarHeader className={cn('shrink-0 justify-start gap-0 px-6 pt-7 pb-0', isSidebarCollapsed && 'items-center px-0 pt-4')}>
          <DynamicLink aria-label="Uppbeat homepage" className={cn('relative flex items-start', isSidebarCollapsed ? 'h-auto justify-center' : 'h-12')} href={homepageLink} onClick={onCloseSidebar}>
            {/* h-12 w-auto mirrors the homepage TopNavigation logo sizing so
                the logo doesn't change size by a pixel when navigating off
                the homepage (DEV-6657). */}
            {isSidebarCollapsed ? <UBLogoSmall /> : <UBLogo className="h-12 w-auto" />}
          </DynamicLink>
        </SidebarHeader>

        {/* Navigation links */}
        <SidebarContent className="relative gap-0 overflow-hidden">
          <div className="h-full overflow-y-auto [&::-webkit-scrollbar]:hidden" ref={scrollContainerRef} style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}>
            <AppSidebarLinks />
          </div>

          <FadeAway className={cn('h-12', SIDEBAR_FADE_CLASSES)} direction="top" show={canScrollUp} />

          <FadeAway className={cn('via-[color-mix(in_srgb,var(--ub-sidebar-bg)_90%,transparent)] duration-300', SIDEBAR_FADE_CLASSES)} direction="bottom" show={canScrollDown && !isSidebarCollapsed} style={{
          height: `${gradientHeight}px`
        }} />

          <AnimatePresence>
            {canScrollDown && !isSidebarCollapsed && <motion.div animate={{
            opacity: canScrollDown ? 1 : 0,
            y: canScrollDown ? 0 : 10
          }} className="absolute bottom-0 left-1/2 z-20" exit={{
            opacity: 0,
            y: 10
          }} initial={{
            opacity: 0,
            y: 10
          }} transition={{
            duration: 0.2
          }}>
                <Tooltip delayDuration={1000} label="More options below" side="top">
                  <Button aria-label="Scroll down for more options" borderRadius="full" className="-translate-x-1/2 bg-transparent!" onClick={scrollToBottom} variant="transparent">
                    <ChevronDown className="size-5 animate-bounce" />
                  </Button>
                </Tooltip>
              </motion.div>}
          </AnimatePresence>
        </SidebarContent>

        {/* Footer: collapse toggle, theme, social links */}
        <SidebarFooter className={cn('flex-row items-center justify-between px-4 py-5 group-data-[collapsible=icon]:flex-col-reverse', 'sidebarVisible:flex-col-reverse sidebarVisible:items-center sidebarVisible:justify-start', 'sidebarVisible:group-data-[state=expanded]:flex-row', isAudioPlayerVisible && 'headerFixed:pb-[72px]')}>
          <div className="flex items-center group-data-[collapsible=icon]:flex-col-reverse">
            <Tooltip label={isSidebarToggled ? 'Expand sidebar' : 'Collapse sidebar'} side={isSidebarToggled ? 'right' : 'top'}>
              <Button aria-label="Collapse sidebar" borderRadius="full" className="sidebarVisible:inline-flex hidden size-7 shrink-0" onClick={onToggleSidebar} size="icon" variant="transparent">
                <ChevronsLeft className={cn('size-4 transition-transform duration-300', isSidebarCollapsed && 'rotate-180')} />
              </Button>
            </Tooltip>
            <ThemeToggle />
          </div>
          <div className={cn('flex items-center', 'sidebarVisible:group-data-[state=expanded]:ml-auto', isSidebarCollapsed ? 'flex-col gap-2' : 'gap-1')}>
            <Tooltip label="Follow Uppbeat on Instagram" side={isSidebarToggled ? 'right' : 'top'}>
              <a className="flex size-7 items-center justify-center rounded-full text-gray-800 hover:bg-gray-50 dark:text-white dark:hover:bg-gray-900" href={INSTAGRAM_URL} onClick={onInstagramClick} rel="noreferrer" target="_blank">
                <Instagram className="size-4" />
                <span className="sr-only">Follow Uppbeat on Instagram</span>
              </a>
            </Tooltip>
            {!isSidebarCollapsed && <div className="h-4 w-px bg-gray-150 dark:bg-gray-800" />}
            <Tooltip label="Join the Uppbeat Discord server" side={isSidebarToggled ? 'right' : 'top'}>
              <a className="flex size-7 items-center justify-center rounded-full text-gray-800 hover:bg-gray-50 dark:text-white dark:hover:bg-gray-900" href={DISCORD_URL} onClick={onDiscordClick} rel="noreferrer" target="_blank">
                <Discord className="size-4" />
                <span className="sr-only">Join the Uppbeat Discord server</span>
              </a>
            </Tooltip>
          </div>
        </SidebarFooter>

        {/* Safari-specific overlay for search */}
        {isSafari && isSearchVisible && isMd && <motion.div className="absolute inset-0 z-[999] bg-black/20 backdrop-blur-px will-change-[opacity] [backface-visibility:hidden] [transform:translate3d(0,0,0)]" onClick={() => {
        window.dispatchEvent(new CustomEvent('closeGlobalSearch'));
        toggleSearch(false);
        setIsSearchVisible(false);
      }} />}
      </Sidebar>
    </>;
});
AppSidebar.displayName = 'AppSidebar';
export default AppSidebar;